import React, { useEffect, useState } from 'react';
import { TempoResponsavel as TempoResponsavelType } from '../../types';
import tarefaService from '../../services/tarefaService';
import { formatarDuracao } from '../../utils/formatters';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface Props {
  tarefaId: string;
  tarefaStatus?: string;
  dataVencimento?: string | null;
}

const STATUSES_FINALIZADOS = ['Concluída', 'Finalizada'];

const isEmAtraso = (dataVencimento: string | null | undefined, finalizada: boolean): boolean => {
  if (!dataVencimento || finalizada) return false;
  const hoje = new Date();
  const hojeStr = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`;
  return dataVencimento < hojeStr;
};

const TempoResponsavel: React.FC<Props> = ({ tarefaId, tarefaStatus, dataVencimento }) => {
  const [historico, setHistorico] = useState<TempoResponsavelType[]>([]);
  const [, setTick] = useState(0);

  const tarefaFinalizada = tarefaStatus ? STATUSES_FINALIZADOS.includes(tarefaStatus) : false;
  const emAtraso = isEmAtraso(dataVencimento, tarefaFinalizada);

  useEffect(() => {
    tarefaService.obterTempoResponsavel(tarefaId).then(setHistorico);
  }, [tarefaId]);

  useEffect(() => {
    if (tarefaFinalizada) return;
    const temEmAndamento = historico.some(h => h.emAndamento);
    if (!temEmAndamento) return;
    const interval = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, [historico, tarefaFinalizada]);

  const calcularSegundosAtuais = (inicio: string) => {
    return Math.max(0, Math.floor((Date.now() - new Date(inicio).getTime()) / 1000));
  };

  const totalSegundos = historico.reduce((acc, h) => {
    const emAndamento = h.emAndamento && !tarefaFinalizada;
    if (emAndamento) return acc + calcularSegundosAtuais(h.inicio);
    return acc + (Number(h.duracaoSegundos) || 0);
  }, 0);

  const formatarPeriodo = (inicio: string, fim: string | null) => {
    const fmt = (d: string) => format(new Date(d), "dd/MM", { locale: ptBR });
    if (!fim) return `desde ${fmt(inicio)}`;
    return `${fmt(inicio)} → ${fmt(fim)}`;
  };

  if (historico.length === 0) return null;

  return (
    <div className="mt-6 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-sm font-medium text-bravo-secondary">Tempo por Responsável</h3>
        {emAtraso && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
            ⚠ Em atraso
          </span>
        )}
      </div>
      <div className="border border-gray-200 rounded-md overflow-hidden">
        {historico.map((h, i) => {
          const emAndamento = h.emAndamento && !tarefaFinalizada;
          const duracao = emAndamento ? calcularSegundosAtuais(h.inicio) : (Number(h.duracaoSegundos) || 0);
          return (
            <div
              key={h.id}
              className={`flex items-center justify-between px-4 py-3 text-sm ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
            >
              <div className="flex items-center gap-2">
                <span className={emAndamento ? 'text-blue-500' : 'text-gray-400'}>
                  {emAndamento ? '⏱' : '●'}
                </span>
                <span className="font-medium text-bravo-dark">{h.responsavelNome}</span>
                <span className="text-xs text-gray-400">({formatarPeriodo(h.inicio, h.fim)})</span>
              </div>
              <span className={`font-mono text-xs font-semibold ${emAndamento ? 'text-blue-600' : 'text-gray-600'}`}>
                {formatarDuracao(duracao)}
                {emAndamento && <span className="ml-1 text-blue-400 font-normal">em andamento</span>}
              </span>
            </div>
          );
        })}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-100 border-t border-gray-200">
          <span className="text-xs font-semibold text-bravo-secondary uppercase tracking-wide">Tempo total</span>
          <span className={`font-mono text-sm font-bold ${emAtraso ? 'text-red-600' : 'text-bravo-dark'}`}>
            {formatarDuracao(totalSegundos)}
          </span>
        </div>
      </div>
    </div>
  );
};

export const useRecarregarTempo = () => {
  const [key, setKey] = useState(0);
  const recarregar = () => setKey(k => k + 1);
  return { key, recarregar };
};

export default TempoResponsavel;
